<template>
  <div :class="['relative', compact ? 'inline-block' : 'w-full', 'z-50']">
    <button
      ref="buttonRef"
      type="button"
      :class="[
        'group relative rounded-xl bg-gradient-to-br from-green-50 dark:from-slate-700 to-emerald-50 dark:to-slate-800 border-2 border-green-200/50 dark:border-slate-600 hover:border-green-300 dark:hover:border-slate-500 transition-all duration-200 shadow-sm hover:shadow-md',
        compact ? 'flex items-center gap-2 rounded-full px-3 py-1.5' : 'w-full p-3'
      ]"
      :aria-busy="isLanguageLoading"
      :disabled="isLanguageLoading"
      @click="toggleDropdown"
    >
      <div v-if="compact" class="flex items-center justify-center">
        <span class="text-2xl">{{ currentLanguage.flag }}</span>
      </div>
      <div v-else class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 rounded-full bg-green-500 dark:bg-emerald-600 flex items-center justify-center shadow-sm">
            <IconGlobe class="w-4 h-4 text-white" />
          </div>
          <div class="text-left">
            <div
              v-translate="'language'"
              class="text-xs font-medium text-green-600 dark:text-emerald-400 uppercase tracking-wider"
            ></div>
            <div class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ currentLanguage.flag }} {{ currentLanguage.nativeName }}
            </div>
          </div>
        </div>
        <IconChevronDown
          v-if="!isLanguageLoading"
          :class="[
            'w-5 h-5 text-green-600 dark:text-emerald-400 transition-transform duration-200',
            showDropdown ? 'rotate-180' : ''
          ]"
        />
      </div>

      <LoadingSpinner
        v-if="isLanguageLoading"
        class="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600"
        size="small"
      />
    </button>

    <div v-if="showDropdown" :class="dropdownClasses" :style="dropdownStyle">
      <button
        v-for="option in languageOptions"
        :key="option.code"
        type="button"
        :class="[
          'w-full flex items-center gap-3 px-4 py-3 transition-colors hover:bg-green-50 dark:hover:bg-slate-700',
          language === option.code ? 'bg-green-100 dark:bg-slate-700/50' : ''
        ]"
        @click="selectLanguage(option.code)"
      >
        <span class="text-2xl">{{ option.flag }}</span>
        <div class="flex-1 text-left">
          <div :class="['font-medium text-gray-900 dark:text-white', compact ? 'text-sm' : '']">
            {{ option.nativeName }}
          </div>
          <div class="text-xs text-gray-500 dark:text-slate-400">{{ option.name }}</div>
        </div>
        <IconCheck
          v-if="language === option.code"
          :class="['text-green-600 dark:text-emerald-400', compact ? 'w-4 h-4' : 'w-5 h-5']"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { useLanguage, type Language } from "@core/composables/state/useLanguage";
import { useRouterContext } from "@core/composables/state/useRouteContext";
import IconCheck from "@core/icons/IconCheck.vue";
import IconChevronDown from "@core/icons/IconChevronDown.vue";
import IconGlobe from "@core/icons/IconGlobe.vue";

import LoadingSpinner from "./LoadingSpinner.vue";

interface Props {
  compact?: boolean;
  dropdownDirection?: "bottom" | "right" | "left";
}

const { language, currentLanguage, languageOptions, setLanguage, isLanguageLoading } = useLanguage();
const showDropdown = ref(false);
const buttonRef = ref<HTMLElement>();
const dropdownTop = ref("0px");
const router = useRouterContext();
let removeAfterEach: (() => void) | undefined;
const props = withDefaults(defineProps<Props>(), {
  compact: false,
  dropdownDirection: "bottom"
});

const dropdownClasses = computed(() => {
  const baseClasses =
    "z-[60] bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden transition-colors duration-300";

  if (props.compact && props.dropdownDirection === "right") {
    return `fixed left-20 w-56 ${baseClasses}`;
  }

  const classes = [`absolute ${baseClasses}`];

  if (props.compact) {
    if (props.dropdownDirection === "left") {
      classes.push("right-full top-0 mr-3 w-56");
      return classes.join(" ");
    }

    classes.push("mt-2 top-full right-0 w-56");
    return classes.join(" ");
  }

  if (props.dropdownDirection === "right") {
    classes.push("ml-3 left-full top-0 w-full");
    return classes.join(" ");
  }

  if (props.dropdownDirection === "left") {
    classes.push("mr-3 right-full top-0 w-full");
    return classes.join(" ");
  }

  classes.push("mt-2 top-full left-0 w-full");
  return classes.join(" ");
});

const dropdownStyle = computed(() => {
  if (props.compact && props.dropdownDirection === "right") {
    return { top: dropdownTop.value };
  }
  return {};
});

const toggleDropdown = (event: MouseEvent) => {
  if (isLanguageLoading.value) {
    return;
  }
  if (!showDropdown.value && props.compact && props.dropdownDirection === "right") {
    const top = event.clientY || parseFloat(dropdownTop.value) || window.innerHeight * 0.1;
    dropdownTop.value = `${top}px`;
  }
  showDropdown.value = !showDropdown.value;
};

const selectLanguage = (lang: Language) => {
  setLanguage(lang);
  showDropdown.value = false;
};

watch(
  () => props.compact,
  () => {
    showDropdown.value = false;
  }
);

onMounted(() => {
  removeAfterEach = router.afterEach(() => {
    showDropdown.value = false;
  });
});

onBeforeUnmount(() => {
  if (removeAfterEach) {
    removeAfterEach();
    removeAfterEach = undefined;
  }
});
</script>
